import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { Card, Button, EmptyState, Table, TableHead, TableBody, TableRow, TableCell } from '../../components/UI';
import {
  dashboardAPI, reportsAPI, callLogsAPI, pipelineAPI,
  attendanceAPI, allocationAPI
} from '../../services/api';
import {
  HiOutlineTrendingUp, HiOutlineTrendingDown,
  HiOutlinePhone, HiOutlinePhoneIncoming,
  HiOutlineDocumentReport, HiOutlineClipboardCheck, HiOutlineClipboardList,
  HiOutlineCheckCircle, HiOutlineExclamationCircle, HiOutlineXCircle,
  HiOutlineChartBar, HiOutlineChartPie,
  HiOutlineDownload, HiOutlineFire, HiOutlineLightBulb, HiOutlineUserGroup,
  HiOutlineArrowRight, HiOutlineExclamation
} from 'react-icons/hi';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { relabelPendingAction, fixAlertGrammar } from '../../utils/formatLabel';

const DATE_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'custom', label: 'Custom' },
];

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

const getDateParams = (range, startDate, endDate) => {
  const params = {};
  if (range === 'today') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    params.startDate = today.toISOString();
  } else if (range === 'yesterday') {
    const y = new Date(); y.setDate(y.getDate() - 1); y.setHours(0, 0, 0, 0);
    params.startDate = y.toISOString();
  } else if (range === 'week') {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'month') {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'quarter') {
    const d = new Date(); const q = Math.floor(d.getMonth() / 3); d.setMonth(q * 3); d.setDate(1); d.setHours(0, 0, 0, 0);
    params.startDate = d.toISOString();
  } else if (range === 'custom') {
    if (startDate) { const s = new Date(startDate); s.setHours(0, 0, 0, 0); params.startDate = s.toISOString(); }
    if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); params.endDate = e.toISOString(); }
  }
  return params;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatHoursMinutes = (hours) => {
  if (!hours) return '0h 0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const StatCard = ({ title, value, icon, trend, trendValue, color }) => {
  const isUp = trend === 'up';
  return (
    <Card padding="p-3 sm:p-4" className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-gray-500 font-medium leading-snug line-clamp-2" title={title}>{title}</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
          {trendValue && (
            <span className={`text-xs font-medium inline-flex items-center gap-0.5 mt-1 ${isUp ? 'text-green-600' : 'text-red-600'}`}>
              {isUp ? <HiOutlineTrendingUp className="w-3 h-3" /> : <HiOutlineTrendingDown className="w-3 h-3" />}
              {trendValue}
            </span>
          )}
        </div>
        {icon && (
          <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${
            color === 'primary' ? 'from-primary-500 to-primary-600' :
            color === 'green' ? 'from-green-500 to-green-600' :
            color === 'blue' ? 'from-blue-500 to-blue-600' :
            color === 'purple' ? 'from-purple-500 to-purple-600' :
            color === 'orange' ? 'from-orange-500 to-orange-600' :
            color === 'red' ? 'from-red-500 to-red-600' :
            'from-indigo-500 to-indigo-600'
          } shadow-md`}>
            <span className="text-white">{icon}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

const CEODashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const dateParams = useMemo(() => getDateParams(range, startDate, endDate), [range, startDate, endDate]);
  const queryParams = { range, ...dateParams };

  // Fetch all data
  const { data: statsData } = useQuery({
    queryKey: ['ceoDashboardStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await dashboardAPI.getStats(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: teamSummary } = useQuery({
    queryKey: ['ceoTeamSummary', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getTeamSummary(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: leaderboard } = useQuery({
    queryKey: ['ceoLeaderboard', range],
    queryFn: async () => { try { const r = await dashboardAPI.getLeaderboard({ period: range, sortBy: 'totalCalls' }); return r.data || { data: [], summary: {} }; } catch { return { data: [], summary: {} }; } },
  });

  const { data: callStatusDist } = useQuery({
    queryKey: ['ceoCallStatusDist', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getCallStatusDistribution(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: callStats } = useQuery({
    queryKey: ['ceoCallStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await callLogsAPI.getStatistics({ source: 'app', ...queryParams }); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: pipelineStats } = useQuery({
    queryKey: ['ceoPipelineStats'],
    queryFn: async () => { try { const r = await pipelineAPI.getStats(); return r.data?.data || []; } catch { return []; } },
  });

  const { data: leadSources } = useQuery({
    queryKey: ['ceoLeadSources', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getLeadSources(queryParams); return r.data?.data || []; } catch { return []; } },
  });

  const { data: productPerformance } = useQuery({
    queryKey: ['ceoProductPerformance', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getProductPerformance(queryParams); return r.data?.data || []; } catch { return []; } },
  });

  const { data: followUpStats } = useQuery({
    queryKey: ['ceoFollowUpStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getFollowUpStats(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: followUpDist } = useQuery({
    queryKey: ['ceoFollowUpDist', range, startDate, endDate],
    queryFn: async () => { try { const r = await reportsAPI.getFollowUpStatusDistribution(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: attendanceData } = useQuery({
    queryKey: ['ceoAttendanceStats', range, startDate, endDate],
    queryFn: async () => { try { const r = await attendanceAPI.getStats(queryParams); return r.data?.data || {}; } catch { return {}; } },
  });

  const { data: weeklyTrends } = useQuery({
    queryKey: ['ceoWeeklyTrends'],
    queryFn: async () => { try { const r = await reportsAPI.getWeeklyTrends(); return r.data?.data || []; } catch { return []; } },
  });

  const { data: alerts } = useQuery({
    queryKey: ['ceoAlerts'],
    queryFn: async () => { try { const r = await dashboardAPI.getAlerts?.(); const list = r?.data?.data || []; return Array.isArray(list) ? list.map(a => ({ ...a, title: fixAlertGrammar(a.title) })) : []; } catch { return []; } },
  });

  const { data: pendingActions } = useQuery({
    queryKey: ['ceoPendingActions', range, startDate, endDate],
    queryFn: async () => { try { const r = await dashboardAPI.getPendingActions?.(queryParams); const list = r?.data?.data || []; return Array.isArray(list) ? list.map(a => ({ ...a, name: relabelPendingAction(a.name) })) : []; } catch { return []; } },
  });

  const { data: allocationData } = useQuery({
    queryKey: ['ceoAllocation'],
    queryFn: async () => { try { const r = await allocationAPI.getAvailable(); return r.data?.data || {}; } catch { return {}; } },
  });

  // Derived data
  const stats = statsData || {};
  const callAnalytics = stats.callAnalytics || {};
  const callTypeData = stats.callTypeData || {};
  const leaderboardData = leaderboard?.data || [];

  const callStatusData = useMemo(() => {
    // Backend returns an array [{ name, value, color }]; keep legacy object shape as fallback
    if (Array.isArray(callStatusDist)) {
      return callStatusDist
        .map((d, idx) => ({ name: d.name || 'Unknown', value: Number(d.value) || 0, color: d.color || PIE_COLORS[idx % PIE_COLORS.length] }))
        .filter(d => d.value > 0);
    }
    const raw = callStatusDist || {};
    const labels = { connected: 'Connected', not_connected: 'Not Connected', no_answer: 'No Answer', busy: 'Busy', switched_off: 'Switched Off', wrong_number: 'Wrong Number', not_reachable: 'Not Reachable' };
    const colors = { connected: '#22c55e', not_connected: '#ef4444', no_answer: '#f59e0b', busy: '#3b82f6', switched_off: '#8b5cf6', wrong_number: '#6b7280', not_reachable: '#ec4899' };
    return Object.entries(labels).map(([key, name]) => ({ name, value: raw[key] || 0, color: colors[key] })).filter(d => d.value > 0);
  }, [callStatusDist]);

  const followUpDistData = useMemo(() => {
    if (Array.isArray(followUpDist)) {
      return followUpDist
        .map((d, idx) => ({ name: d.name || 'Unknown', value: Number(d.value) || 0, color: d.color || PIE_COLORS[idx % PIE_COLORS.length] }))
        .filter(d => d.value > 0);
    }
    const raw = followUpDist || {};
    const labels = { completed: 'Completed', pending: 'Pending', overdue: 'Overdue', cancelled: 'Cancelled' };
    const colors = { completed: '#22c55e', pending: '#f59e0b', overdue: '#ef4444', cancelled: '#6b7280' };
    return Object.entries(labels).map(([key, name]) => ({ name, value: raw[key] || 0, color: colors[key] })).filter(d => d.value > 0);
  }, [followUpDist]);

  const pipelineData = useMemo(() => {
    if (!Array.isArray(pipelineStats)) return [];
    return pipelineStats.map(s => ({ name: s.name || s.key, value: s.leadsCount || 0, color: s.color || '#6B7280' })).filter(d => d.value > 0);
  }, [pipelineStats]);

  const leadSourceData = useMemo(() => {
    if (!Array.isArray(leadSources)) return [];
    return leadSources.map(s => ({ name: s.name || 'Unknown', value: s.leads || s.value || 0, color: s.color || '#6B7280' }));
  }, [leadSources]);

  const fuStats = followUpStats || {};
  const fuTotal = (fuStats.completed || 0) + (fuStats.pending || 0) + (fuStats.overdue || 0) + (fuStats.cancelled || 0);
  const attStats = attendanceData || {};

  // KPI values — all read from the same (range-scoped) dashboard response so they agree with each other
  const kpiTotalLeads = stats.allTimeTotalLeads ?? stats.totalLeads ?? 0;
  const kpiTotalCalls = stats.calls?.total || 0;
  const kpiConnectedCalls = stats.calls?.connected || 0;
  const connectionRate = kpiTotalCalls > 0 ? Math.round((kpiConnectedCalls / kpiTotalCalls) * 100) : 0;

  // Weekly trends: backend returns [{ name, target, achieved, connected, conversions }]
  const weeklyTrendData = useMemo(() => {
    if (!Array.isArray(weeklyTrends)) return [];
    return weeklyTrends.map((w, idx) => ({
      label: w.name || w.label || w.week || w.date || `Week ${idx + 1}`,
      target: w.target ?? null,
      callsMade: w.achieved ?? w.callsMade ?? 0,
      connectedCalls: w.connected ?? w.connectedCalls ?? 0,
      conversions: w.conversions ?? 0,
    }));
  }, [weeklyTrends]);

  // Lead allocation: new contract { availableLeads, unassignedLeads, totalAllocated, totalLeads }
  const allocation = useMemo(() => {
    const a = allocationData || {};
    const unassigned = a.unassignedLeads ?? a.availableLeads ?? a.available ?? a.count ?? 0;
    const total = a.totalLeads ?? a.total ?? null;
    const allocated = a.totalAllocated ?? a.allocated ?? (total != null ? Math.max(total - unassigned, 0) : null);
    return { unassigned, allocated, total };
  }, [allocationData]);

  const handleExport = async () => {
    toast.loading('Generating CEO report...');
    try {
      // Export using daily work report as base
      const response = await reportsAPI.exportDailyWork({ date: format(new Date(), 'yyyy-MM-dd') });
      const data = response.data?.data || response.data || [];
      if (!data || (Array.isArray(data) && data.length === 0)) {
        toast.dismiss(); toast.error('No data to export'); return;
      }
      const headers = Object.keys(Array.isArray(data) ? data[0] : data);
      const csvRows = [headers.join(','), ...(Array.isArray(data) ? data : [data]).map(row => headers.map(h => `"${row[h] || ''}"`).join(','))];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url); link.setAttribute('download', `ceo-dashboard-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
      toast.dismiss(); toast.success('Report exported successfully');
    } catch { toast.dismiss(); toast.error('Failed to export report'); }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <HiOutlineChartBar className="w-5 h-5 text-primary-600" />
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">CEO Dashboard</h1>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">Executive overview of all operations</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none"
            >
              {DATE_RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            {range === 'custom' && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none" />
              </>
            )}
            <Button variant="outline" size="sm" icon={<HiOutlineDownload className="w-4 h-4" />} onClick={handleExport}>
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 lg:gap-4">
          <StatCard title="Total Leads" value={kpiTotalLeads} icon={<HiOutlineDocumentReport className="w-5 h-5" />} color="primary" trendValue={stats.leadTrend} trend={stats.leadTrend === 'up' ? 'up' : 'down'} />
          <StatCard title="Total Calls" value={kpiTotalCalls} icon={<HiOutlinePhone className="w-5 h-5" />} color="blue" trendValue={stats.callsTrend} trend={stats.callsTrend === 'up' ? 'up' : 'down'} />
          <StatCard title="Connection Rate" value={`${connectionRate}%`} icon={<HiOutlinePhoneIncoming className="w-5 h-5" />} color="green" />
          <StatCard title="Converted" value={stats.convertedLeads || 0} icon={<HiOutlineCheckCircle className="w-5 h-5" />} color="purple" />
          <StatCard title="Follow-ups Due Today" value={stats.followUpsDueToday || 0} icon={<HiOutlineClipboardList className="w-5 h-5" />} color="orange" />
          <StatCard title="Hot Leads" value={stats.hotLeads || 0} icon={<HiOutlineFire className="w-5 h-5" />} color="red" />
        </div>

        {/* Team Performance Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Team Summary */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Team Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="text-center p-2.5 sm:p-3 bg-primary-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-primary-600">{teamSummary?.activeUsers || stats.totalUsers || 0}</div>
                <div className="text-xs text-gray-500">Active Agents</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-blue-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-blue-600">{teamSummary?.totalCalls || callStats?.total || 0}</div>
                <div className="text-xs text-gray-500">Total Calls</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-green-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-green-600">{teamSummary?.totalConnected || callStats?.connected || 0}</div>
                <div className="text-xs text-gray-500">Connected</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-purple-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-purple-600">{teamSummary?.conversionCount || stats.convertedLeads || 0}</div>
                <div className="text-xs text-gray-500">Converted</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-orange-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-orange-600">{formatHoursMinutes(teamSummary?.totalWorkingHours || 0)}</div>
                <div className="text-xs text-gray-500">Working Hrs</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-indigo-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-indigo-600">{formatDuration(callAnalytics.averageDuration || callStats?.averageDuration || 0)}</div>
                <div className="text-xs text-gray-500">Avg Duration</div>
              </div>
            </div>
          </Card>

          {/* Top Performers */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Top Performers</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/leaderboard')} className="text-xs">
                View All <HiOutlineArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            {!leaderboardData || leaderboardData.length === 0 ? (
              <EmptyState icon={<HiOutlineUserGroup className="w-8 h-8 text-gray-400" />} title="No data" description="No performer data for this period" />
            ) : (
              <div className="space-y-2">
                {leaderboardData.slice(0, 5).map((user, idx) => (
                  <div key={user.userId || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : idx === 1 ? 'bg-gray-100 text-gray-600' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-500'}`}>
                      {idx + 1}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-600 font-medium text-xs">{(user.userName || user.name || '').split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.userName || user.name}</p>
                      <p className="text-xs text-gray-500">{user.totalCalls || 0} calls · {user.connectedCalls || 0} connected</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-green-600">{user.conversionCount || 0}</p>
                      <p className="text-xs text-gray-400">converted</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Call Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Call Status Distribution */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Call Outcomes</h3>
            {callStatusData.length > 0 ? (
              <div className="flex flex-col items-center">
                <div className="w-full h-48 sm:h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={callStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                        {callStatusData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
                  {callStatusData.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                      <span className="text-xs text-gray-500">{entry.name} ({entry.value})</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState icon={<HiOutlinePhone className="w-8 h-8 text-gray-400" />} title="No call data" description="No call outcomes for this period" />
            )}
          </Card>

          {/* Call Duration Stats */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Call Duration Analytics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="text-center p-3 bg-green-50 rounded-xl">
                <div className="text-lg sm:text-xl font-bold text-green-600">{formatDuration(callAnalytics.averageDuration || callStats?.averageDuration || 0)}</div>
                <div className="text-xs text-gray-500 mt-1">Avg Duration</div>
                <div className="text-xs text-green-500">per connected call</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <div className="text-lg sm:text-xl font-bold text-blue-600">{formatDuration(callAnalytics.totalTalkTime || callStats?.totalDuration || 0)}</div>
                <div className="text-xs text-gray-500 mt-1">Total Talk Time</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-xl">
                <div className="text-lg sm:text-xl font-bold text-purple-600">{formatDuration(callAnalytics.maxDuration || 0)}</div>
                <div className="text-xs text-gray-500 mt-1">Longest Call</div>
              </div>
            </div>
            {/* Call type breakdown */}
            {callTypeData && (callTypeData.outgoing || callTypeData.incoming || callTypeData.missed) && (
              <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100">
                <div className="text-center">
                  <div className="text-sm font-semibold text-blue-600">{callTypeData.outgoing?.count || 0}</div>
                  <div className="text-xs text-gray-500">Outgoing</div>
                  <div className="text-xs text-green-500">{callTypeData.outgoing?.connected || 0} connected</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-indigo-600">{callTypeData.incoming?.count || 0}</div>
                  <div className="text-xs text-gray-500">Incoming</div>
                  <div className="text-xs text-green-500">{callTypeData.incoming?.connected || 0} connected</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-red-600">{callTypeData.missed?.count || 0}</div>
                  <div className="text-xs text-gray-500">Missed</div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Lead Pipeline & Sources */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Lead Pipeline */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Lead Pipeline</h3>
            {pipelineData.length > 0 ? (
              <div className="space-y-2.5">
                {pipelineData.map((stage, idx) => {
                  const maxVal = Math.max(...pipelineData.map(s => s.value), 1);
                  const pct = Math.round((stage.value / maxVal) * 100);
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="font-medium text-gray-700">{stage.name}</span>
                        <span className="text-gray-900 font-semibold">{stage.value}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: stage.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={<HiOutlineDocumentReport className="w-8 h-8 text-gray-400" />} title="No pipeline data" />
            )}
          </Card>

          {/* Lead Sources */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Lead Sources</h3>
            {leadSourceData.length > 0 ? (
              <div className="flex flex-col items-center">
                <div className="w-full h-48 sm:h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={leadSourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" nameKey="name">
                        {leadSourceData.map((entry, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
                  {leadSourceData.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                      <span className="text-xs text-gray-500">{entry.name} ({entry.value})</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState icon={<HiOutlineChartPie className="w-8 h-8 text-gray-400" />} title="No source data" />
            )}
          </Card>
        </div>

        {/* Product Performance */}
        {Array.isArray(productPerformance) && productPerformance.length > 0 && (
          <Card padding="p-0">
            <div className="p-3 sm:p-4 lg:p-5 border-b border-gray-100">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Product Performance</h3>
            </div>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>Product</TableCell>
                    <TableCell header>Leads</TableCell>
                    <TableCell header>Calls</TableCell>
                    <TableCell header>Connected</TableCell>
                    <TableCell header>Converted</TableCell>
                    <TableCell header>Conv. Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productPerformance.slice(0, 10).map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell><span className="font-medium text-gray-900">{p.name || p.product || '—'}</span></TableCell>
                      <TableCell>{p.leads || 0}</TableCell>
                      <TableCell>{p.calls || 0}</TableCell>
                      <TableCell>{p.connected || 0}</TableCell>
                      <TableCell><span className="text-green-600 font-medium">{p.converted || 0}</span></TableCell>
                      <TableCell>{p.leads > 0 ? `${Math.round(((p.converted || 0) / p.leads) * 100)}%` : '0%'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {productPerformance.slice(0, 5).map((p, idx) => (
                <div key={idx} className="p-3">
                  <p className="font-medium text-gray-900 text-sm">{p.name || p.product || '—'}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-1.5 text-center"><div className="font-semibold">{p.leads || 0}</div><div className="text-gray-500">Leads</div></div>
                    <div className="bg-gray-50 rounded-lg p-1.5 text-center"><div className="font-semibold text-green-600">{p.converted || 0}</div><div className="text-gray-500">Conv.</div></div>
                    <div className="bg-gray-50 rounded-lg p-1.5 text-center"><div className="font-semibold">{p.leads > 0 ? `${Math.round(((p.converted || 0) / p.leads) * 100)}%` : '0%'}</div><div className="text-gray-500">Rate</div></div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Follow-up & Attendance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Follow-up Stats */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Follow-up Overview</h3>
            {fuTotal > 0 ? (
              <div className="space-y-3">
                {[
                  { label: 'Completed', value: fuStats.completed || 0, color: 'bg-green-500', bg: 'bg-green-50', textColor: 'text-green-600', icon: <HiOutlineCheckCircle className="w-4 h-4 text-green-600" /> },
                  { label: 'Pending', value: fuStats.pending || 0, color: 'bg-yellow-500', bg: 'bg-yellow-50', textColor: 'text-yellow-600', icon: <HiOutlineExclamationCircle className="w-4 h-4 text-yellow-600" /> },
                  { label: 'Overdue', value: fuStats.overdue || 0, color: 'bg-red-500', bg: 'bg-red-50', textColor: 'text-red-600', icon: <HiOutlineExclamationCircle className="w-4 h-4 text-red-600" /> },
                  { label: 'Cancelled', value: fuStats.cancelled || 0, color: 'bg-gray-400', bg: 'bg-gray-50', textColor: 'text-gray-600', icon: <HiOutlineXCircle className="w-4 h-4 text-gray-500" /> },
                ].map((item) => {
                  const pct = fuTotal > 0 ? Math.round((item.value / fuTotal) * 100) : 0;
                  return (
                    <div key={item.label} className="flex items-center gap-3 p-2.5 rounded-xl">
                      <div className={`w-9 h-9 rounded-full ${item.bg} flex items-center justify-center flex-shrink-0`}>{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{item.label}</span>
                          <span className={`text-sm font-bold ${item.textColor}`}>{item.value}</span>
                        </div>
                        <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Donut chart */}
                {followUpDistData.length > 0 && (
                  <div className="flex justify-center mt-3">
                    <div className="w-40 h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={followUpDistData} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={2} dataKey="value" nameKey="name">
                            {followUpDistData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [v, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState icon={<HiOutlineClipboardList className="w-8 h-8 text-gray-400" />} title="No follow-up data" />
            )}
          </Card>

          {/* Attendance Summary */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Attendance Summary</h3>
            {attStats.totalUsers > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="text-center p-2.5 bg-green-50 rounded-xl">
                    <div className="text-lg font-bold text-green-600">{attStats.present || 0}</div>
                    <div className="text-xs text-gray-500">Present</div>
                  </div>
                  <div className="text-center p-2.5 bg-red-50 rounded-xl">
                    <div className="text-lg font-bold text-red-600">{attStats.absent || 0}</div>
                    <div className="text-xs text-gray-500">Absent</div>
                  </div>
                  <div className="text-center p-2.5 bg-yellow-50 rounded-xl">
                    <div className="text-lg font-bold text-yellow-600">{attStats.late || 0}</div>
                    <div className="text-xs text-gray-500">Late</div>
                  </div>
                  <div className="text-center p-2.5 bg-blue-50 rounded-xl">
                    <div className="text-lg font-bold text-blue-600">{attStats.halfDay || 0}</div>
                    <div className="text-xs text-gray-500">Half Day</div>
                  </div>
                </div>
                {attStats.totalUsers > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100 text-center">
                    <div className="text-sm text-gray-600">
                      Attendance Rate: <span className="font-bold text-green-600">{Math.round(((attStats.present || 0) / attStats.totalUsers) * 100)}%</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState icon={<HiOutlineClipboardCheck className="w-8 h-8 text-gray-400" />} title="No attendance data" />
            )}
          </Card>
        </div>

        {/* Weekly Trends */}
        {weeklyTrendData.length > 0 && (
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Weekly Trends</h3>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrendData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                  <Legend />
                  <Line type="monotone" dataKey="target" name="Call Target" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  <Line type="monotone" dataKey="callsMade" name="Calls Made" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="connectedCalls" name="Connected" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="conversions" name="Conversions" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Alerts & Pending Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* CEO Alerts */}
          <Card padding="p-3 sm:p-4 lg:p-5">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">CEO Alerts</h3>
            {Array.isArray(alerts) && alerts.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div
                    key={alert.id || idx}
                    onClick={() => alert.link && navigate(alert.link)}
                    className={`flex items-center gap-3 p-2.5 rounded-xl border hover:shadow-sm transition-shadow ${
                      alert.link ? 'cursor-pointer' : ''
                    } ${
                      alert.type === 'danger' ? 'bg-red-50 border-red-100' :
                      alert.type === 'warning' ? 'bg-orange-50 border-orange-100' :
                      alert.type === 'caution' ? 'bg-amber-50 border-amber-100' :
                      alert.type === 'success' ? 'bg-green-50 border-green-100' :
                      'bg-blue-50 border-blue-100'
                    }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      alert.type === 'danger' ? 'bg-red-500' :
                      alert.type === 'warning' ? 'bg-orange-500' :
                      alert.type === 'caution' ? 'bg-amber-500' :
                      alert.type === 'success' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`}>
                      <HiOutlineExclamation className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate" title={alert.title}>{alert.title}</p>
                      <p className="text-xs text-gray-500 truncate" title={alert.description}>{alert.description}</p>
                    </div>
                    {alert.link && <HiOutlineArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={<HiOutlineLightBulb className="w-8 h-8 text-gray-400" />} title="No alerts" description="Everything is running smoothly" />
            )}
          </Card>

          {/* Pending Actions */}
          {Array.isArray(pendingActions) && pendingActions.length > 0 && (
            <Card padding="p-3 sm:p-4 lg:p-5">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Pending Actions</h3>
              <div className="space-y-2.5">
                {pendingActions.slice(0, 6).map((action, idx) => {
                  const maxVal = Math.max(...pendingActions.map(a => a.value || 0), 1);
                  const pct = Math.round(((action.value || 0) / maxVal) * 100);
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-gray-700">{action.name}</span>
                        <span className="text-gray-900 font-semibold">{action.value}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: action.color || '#6B7280' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {/* Allocation Overview */}
        {allocationData && Object.keys(allocationData).length > 0 && (
          <Card padding="p-3 sm:p-4 lg:p-5">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">Lead Allocation</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/leads/assign')} className="text-xs">
                Manage <HiOutlineArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="text-center p-2.5 sm:p-3 bg-red-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-red-600">{allocation.unassigned}</div>
                <div className="text-xs text-gray-500">Unassigned Leads</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-blue-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-blue-600">{allocation.allocated ?? '—'}</div>
                <div className="text-xs text-gray-500">Allocated</div>
              </div>
              <div className="text-center p-2.5 sm:p-3 bg-green-50 rounded-xl">
                <div className="text-base sm:text-lg font-bold text-green-600">{allocation.total ?? '—'}</div>
                <div className="text-xs text-gray-500">Total Leads</div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CEODashboard;